import React, { useState, useEffect } from "react";
import { Form, Button, Row, Col, Table } from "react-bootstrap";
import { toast } from "react-toastify";

import Utils from "../../configs/Utils";
import Enums from "../../configs/Enums";
import { useAuth } from "../../contexts/AuthContext";

const INITIAL_FORM_DATA = {
  sku: "",
  productName: "",
  imageUrl: "",
};

const ProductForm = ({ workspaceId, setPopupShow, refreshList, selectedProduct }) => {
  const { currentUser } = useAuth();
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [notes, setNotes] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (selectedProduct) {
      setFormData({
        sku: selectedProduct.sku || "",
        productName: selectedProduct.productName || "",
        imageUrl: selectedProduct.imageUrl || "",
      });
      setNotes(
        (selectedProduct.notes || []).map((n) => ({
          _id: n._id,
          date: n.date ? n.date.substring(0, 10) : new Date().toISOString().substring(0, 10),
          user: n.user?._id || n.user,
          userName: n.user?.fullName || n.user?.email || "",
          content: n.content || "",
        }))
      );
      return;
    }
    setFormData(INITIAL_FORM_DATA);
    setNotes([]);
  }, [selectedProduct]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddNote = () => {
    setNotes((prev) => [
      ...prev,
      {
        date: new Date().toISOString().substring(0, 10),
        user: currentUser._id,
        userName: currentUser.fullName || currentUser.email || "",
        content: "",
      },
    ]);
  };

  const handleRemoveNote = (index) => {
    setNotes((prev) => prev.filter((_, i) => i !== index));
  };

  const handleNoteChange = (index, field, value) => {
    setNotes((prev) =>
      prev.map((n, i) => (i === index ? { ...n, [field]: value } : n))
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const notesPayload = notes
        .filter((n) => n.content.trim())
        .map((n) => ({
          ...(n._id ? { _id: n._id } : {}),
          date: n.date,
          user: n.user,
          content: n.content.trim(),
        }));

      const payload = { ...formData, notes: notesPayload, workspace: workspaceId };

      if (selectedProduct) {
        const res = await Utils._apiRequest({
          path: `${Enums.endpoints.supplierStock.PRODUCTS}/${selectedProduct._id}`,
          type: "PUT",
          payload,
        });
        if (res.errors) throw new Error(res.errors);
        toast.success("Cập nhật sản phẩm thành công");
      } else {
        const res = await Utils._apiRequest({
          path: Enums.endpoints.supplierStock.PRODUCTS,
          type: "POST",
          payload,
        });
        if (res.errors) throw new Error(res.errors);
        toast.success("Tạo sản phẩm thành công");
      }

      refreshList();
      setPopupShow(false);
    } catch (error) {
      toast.error(error.message || "Lỗi khi lưu sản phẩm");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit} className="supplier-stock-form">
      <section className="supplier-stock-form__intro">
        <span className="supplier-stock-form__eyebrow">
          {selectedProduct ? "Chỉnh sửa sản phẩm" : "Tạo sản phẩm mới"}
        </span>
        <h3>{selectedProduct ? "Cập nhật thông tin sản phẩm" : "Thêm sản phẩm mới"}</h3>
        <p>Nhập thông tin sản phẩm: SKU, tên, và hình ảnh.</p>
      </section>

      <section className="supplier-stock-form__section supplier-stock-form__meta">
        <Row className="g-3">
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>SKU *</Form.Label>
              <Form.Control
                type="text"
                name="sku"
                value={formData.sku}
                onChange={handleChange}
                placeholder="VD: SHOE-001"
                required
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Tên sản phẩm *</Form.Label>
              <Form.Control
                type="text"
                name="productName"
                value={formData.productName}
                onChange={handleChange}
                placeholder="VD: Giày thể thao Nike"
                required
              />
            </Form.Group>
          </Col>
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Label>Link hình ảnh</Form.Label>
              <Form.Control
                type="text"
                name="imageUrl"
                value={formData.imageUrl}
                onChange={handleChange}
                placeholder="https://example.com/image.jpg"
              />
            </Form.Group>
            {formData.imageUrl && (
              <div className="supplier-stock-form__image-preview">
                <img src={formData.imageUrl} alt="Preview" />
              </div>
            )}
          </Col>
        </Row>
      </section>

      {/* Notes section */}
      <section className="supplier-stock-form__section supplier-stock-form__notes mt-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0">Ghi chú</h5>
          <Button variant="outline-primary" size="sm" onClick={handleAddNote} type="button">
            + Thêm ghi chú
          </Button>
        </div>
        {notes.length > 0 && (
          <Table bordered size="sm" responsive>
            <thead>
              <tr>
                <th style={{ width: "150px" }}>Ngày</th>
                <th style={{ width: "160px" }}>Người thêm</th>
                <th>Nội dung</th>
                <th style={{ width: "60px" }}></th>
              </tr>
            </thead>
            <tbody>
              {notes.map((note, idx) => (
                <tr key={idx}>
                  <td>
                    <Form.Control
                      type="date"
                      value={note.date}
                      onChange={(e) => handleNoteChange(idx, "date", e.target.value)}
                      size="sm"
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="text"
                      value={note.userName}
                      size="sm"
                      disabled
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="text"
                      value={note.content}
                      onChange={(e) => handleNoteChange(idx, "content", e.target.value)}
                      placeholder="Nhập nội dung ghi chú"
                      size="sm"
                    />
                  </td>
                  <td className="text-center">
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleRemoveNote(idx)}
                      type="button"
                    >
                      ✕
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        {notes.length === 0 && (
          <p className="text-muted small mb-0">Chưa có ghi chú nào.</p>
        )}
      </section>

      <div className="d-grid gap-2 d-md-flex justify-content-md-end mt-4 supplier-stock-form__actions">
        <Button
          variant="secondary"
          className="me-md-2"
          onClick={() => setPopupShow(false)}
          disabled={submitting}
          type="button"
        >
          Huỷ
        </Button>
        <Button variant="primary" type="submit" disabled={submitting}>
          {submitting ? "Đang lưu..." : "Lưu sản phẩm"}
        </Button>
      </div>
    </Form>
  );
};

export default ProductForm;
